/* eslint-disable react/no-unescaped-entities */
import { FunctionComponent, memo, useEffect } from 'react'
import { reaction } from 'mobx'
import { Link, Typography } from '@mui/material'
import { useClientDetails } from 'stores/utils/hooks/useClientDetails'
import { getEnv } from 'utils/env'
import useEmotionStyles from 'hooks/useEmotionStyles'
import { TrackEventData } from '../../utils/tracking/tracking.interface'
import { pushGTMEvent } from '../../utils/tracking'
import PathCheckboxYesNo from '../PathCheckboxYesNo'
import { useStores } from '../../stores/utils/hooks/useStores'
import { trackInAmplitude } from 'utils/tracking/amplitude'
import { AmplitudeEvent } from 'utils/tracking/amplitude/amplitude.interface'

const styles = () => ({
  paragraph: {
    color: '#323232', // grey.900
    lineHeight: '1.25rem', // 20px
    fontSize: '.875rem', // 14px
    letterSpacing: '0.02em' // 0.32px
  }
})
interface LabelProps {
  consentUrl: string
}

const Label: FunctionComponent<LabelProps> = memo(({ consentUrl }) => {
  const { paragraph } = useEmotionStyles(styles)
  return (
    <span className={paragraph}>
      Ich möchte zudem über günstige Kreditangebote und Services von FINANZCHECK
      informiert bleiben und willige in die Verwendung meiner E-Mail-Adresse und
      meiner Telefonnummer zur Kontaktaufnahme ein. Die{' '}
      <Link
        href={consentUrl}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() =>
          trackInAmplitude(AmplitudeEvent.OnNewsletterConsentLinkClicked)
        }
      >
        Einwilligung
      </Link>{' '}
      ist freiwillig und kann jederzeit mit Wirkung für die Zukunft widerrufen
      werden.
    </span>
  )
})

Label.displayName = 'Label'

interface AgbConsentParagraphProps {
  boldLinks?: boolean
}

export const AgbConsentParagraph: FunctionComponent<AgbConsentParagraphProps> =
  memo(({ boldLinks = false }) => {
    const clientDetails = useClientDetails()
    const { paragraph } = useEmotionStyles(styles)
    const linkSx = boldLinks ? { fontWeight: 700 } : undefined

    return (
      <p className={paragraph}>
        Mit Klicken des "Weiter"-Buttons erteile ich FINANZCHECK einen
        kostenlosen Kreditvergleichsauftrag und akzeptiere die{' '}
        <Link
          href={clientDetails?.urls?.termsAndConditions}
          target="_blank"
          rel="noopener noreferrer"
          sx={linkSx}
        >
          AGB
        </Link>
        . <br />
        Die{' '}
        <Link
          href={clientDetails?.urls?.precontractual}
          target="_blank"
          rel="noopener noreferrer"
          sx={linkSx}
        >
          Pflichtinformationen
        </Link>{' '}
        und die{' '}
        <Link
          href={clientDetails?.urls?.dataProtection}
          target="_blank"
          rel="noopener noreferrer"
          sx={linkSx}
        >
          Datenschutzhinweise
        </Link>{' '}
        habe ich erhalten.
      </p>
    )
  })

AgbConsentParagraph.displayName = 'AgbConsentParagraph'

const Agb = ({ isHeadlineHidden = false, isNewsLetterHidden = false }) => {
  const { traversal } = useStores()
  const dataProcessingConsent = getEnv(
    'REACT_APP_LEGAL_DATA_PROCESSING_CONSENT_URL',
    true
  )
  useEffect(() => {
    reaction(
      () => traversal.data.common.dataProcessingConsent,
      () => {
        const event: {
          'contact-data-processing-consent': number
        } & TrackEventData = {
          event: 'wsContactDataProcessingConsentCheck',
          'contact-data-processing-consent': Number(
            traversal.data.common.dataProcessingConsent
          )
        }
        pushGTMEvent(event)
      }
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  return (
    <>
      <>
        {!isHeadlineHidden && (
          <Typography variant="h2">Informationen zum Kreditnehmer</Typography>
        )}
        <AgbConsentParagraph />
        {!isNewsLetterHidden && (
          <PathCheckboxYesNo
            path="common.dataProcessingConsent"
            label={<Label consentUrl={dataProcessingConsent} />}
          />
        )}
      </>
    </>
  )
}
export default memo(Agb)
